
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  auth,
  db,
  serverTimestamp,
  type User,
  type UserProfileData,
  type CurrentUser
} from './firebase.ts';
import { blogPosts as staticPostsData } from './constants.tsx';

import Header from './components/Header.tsx';
import Hero from './components/Hero.tsx';
import Features from './components/Features.tsx';
import FeatureList from './components/FeatureList.tsx';
import Testimonials from './components/Testimonials.tsx';
import Clients from './components/Clients.tsx';
import Support from './components/Support.tsx';
import Blog from './components/Blog.tsx';
import CTA from './components/Cta.tsx';
import Pricing from './components/Pricing.tsx';
import Footer from './components/Footer.tsx';
import BackToTopButton from './components/BackToTopButton.tsx';
import LiveAiPicks from './components/LiveAiPicks.tsx';
import TopTenMarketStats from './components/TopTenMarketStats.tsx';
import AuthModal from './components/AuthModal.tsx';
import AIFloatingWidget from './components/AIFloatingWidget.tsx';
import SaraChatWidget from './components/SaraChatWidget.tsx';
import FraudWarningModal, { FraudNoticeBar } from './components/FraudWarningModal.tsx';
import NewsPulse from './components/NewsPulse.tsx';
import SaraChatPage from './components/SaraChatPage.tsx';
import BlogPage from './components/BlogPage.tsx';
import BlogPostPage from './components/BlogPostPage.tsx';
import CreatePostPage from './components/CreatePostPage.tsx';
import PaymentResult from './components/PaymentResult.tsx';

// --- DEBUG: Log module execution start ---
console.log('[App.tsx] Module script loaded.');

export interface Post {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  imageUrl: string;
  author: string;
  authorTitle?: string;
  authorId?: string;
  date: string; // Formatted date string
  tags: string[];
  href?: string; // Static, crawlable page URL when available
  createdAt?: {
    seconds: number;
    nanoseconds: number;
  };
}

const ARTICLES_URL = 'https://news-writer.itsmybadsoul.workers.dev/articles.json';
const COVERS_URL = 'https://news-writer.itsmybadsoul.workers.dev/covers.json';
const FEED_IMAGE_FALLBACK = '/images/news/market.jpg';
const ANALYST_NAME = 'سارة';
const ANALYST_AI_LABEL = 'ذكاء اصطناعي خاص بالمنصة';
const ANALYST_TITLE = 'محللة أسواق — السوق السعودي والأمريكي';
const analystByline = (name?: string) => {
  const n = String(name || '').trim();
  return !n || n === ANALYST_NAME ? `${ANALYST_NAME} (${ANALYST_AI_LABEL})` : n;
};

interface FeedArticle {
  slug: string;
  title: string;
  excerpt?: string;
  body?: string[];
  image?: string;
  author?: string;
  authorTitle?: string;
  date?: string;
  tags?: string[];
  readingMinutes?: number;
}

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

const feedDate = (iso?: string) => {
  const d = new Date(`${String(iso || '').slice(0, 10)}T12:00:00+03:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${AR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const mapFeedArticle = (a: FeedArticle): Post => ({
  id: a.slug,
  title: a.title,
  excerpt: a.excerpt || '',
  content: Array.isArray(a.body) ? a.body.join('\n') : '',
  imageUrl: (a.image || '').replace(/^https?:\/\/tad-ai\.com/, '') || FEED_IMAGE_FALLBACK,
  author: analystByline(a.author),
  authorTitle: a.authorTitle || ANALYST_TITLE,
  date: feedDate(a.date),
  tags: Array.isArray(a.tags) ? a.tags : [],
  href: `/news/${a.slug}/`,
  createdAt: { seconds: new Date(`${String(a.date || '').slice(0, 10)}T12:00:00+03:00`).getTime() / 1000, nanoseconds: 0 },
});

const App: React.FC = () => {
  // --- DEBUG: Log component render ---
  console.log('[App] Component rendering or re-rendering...');

  const { i18n, t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState<'signIn' | 'signUp'>('signIn');
  const [route, setRoute] = useState(window.location.hash || '#home');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const parseRoute = () => {
    // --- DEBUG: Log route parsing ---
    console.log(`[App] Parsing route: ${route}`);
    const hash = route;
    if (hash.startsWith('#/blog/')) {
      const postId = hash.split('/')[2];
      return { page: 'post', params: { id: postId } };
    }
    if (hash.startsWith('#/blog')) {
      return { page: 'blog', params: {} };
    }
    if (hash.startsWith('#/create-post')) {
      return { page: 'create-post', params: {} };
    }
    if (hash.startsWith('#/payment-result') || window.location.pathname.startsWith('/payment-result')) {
      return { page: 'payment-result', params: {} };
    }
    if (hash.startsWith('#/chat') || window.location.pathname.startsWith('/chat')) {
      return { page: 'chat', params: {} };
    }
    return { page: 'home', params: {} };
  }

  const { page, params } = parseRoute();
  // --- DEBUG: Log parsed page and params ---
  console.log(`[App] Current Page: ${page}, Params:`, params);

  useEffect(() => {
    // --- DEBUG: Log language/direction change effect ---
    console.log(`[App] useEffect (i18n.language) running. Setting lang=${i18n.language} and dir=${i18n.dir(i18n.language)}.`);
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir(i18n.language);
  }, [i18n, i18n.language]);

  const fetchAndCombinePosts = useCallback(() => {
    // --- DEBUG: Log post fetching ---
    console.log('[App] useCallback (fetchAndCombinePosts) executed.');
    setLoadingPosts(true);
    // Map static posts
    const staticCovers = ['/images/news/telecom.jpg', '/images/news/energy.jpg', '/images/news/market.jpg'];
    const mappedStaticPosts: Post[] = staticPostsData.map((p, i) => ({
      id: p.id,
      title: t(p.titleKey),
      excerpt: t(p.excerptKey),
      content: t(p.contentKey || p.excerptKey),
      imageUrl: /picsum\.photos/.test(String(p.imageUrl || '')) ? (staticCovers[i % staticCovers.length]) : p.imageUrl,
      author: t(p.authorKey),
      date: p.date,
      tags: p.tagsKeys.map(tagKey => t(tagKey)),
      href: p.slug ? `/news/${p.slug}/` : undefined,
      createdAt: { seconds: new Date(p.date).getTime() / 1000, nanoseconds: 0 }
    }));

    // Fetch dynamic posts from Firestore
    console.log('[App] Setting up Firestore onSnapshot listener for posts.');
    const postsCollection = db.collection('posts');
    const q = postsCollection.orderBy('createdAt', 'desc');

    const feedPosts: Post[] = [];
    let firestorePosts: Post[] = [];
    let coverMap: Record<string, string> = {};
    const withCover = (p: Post): Post => {
      const key = (p.href || '').split('/news/')[1] || p.id;
      const custom = coverMap[key];
      if (!custom) return p;
      return { ...p, imageUrl: String(custom).replace(/^https?:\/\/tad-ai\.com/, '') };
    };
    const combine = () => {
      const allPosts = [...feedPosts, ...firestorePosts, ...mappedStaticPosts].map(withCover);
      allPosts.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setPosts(allPosts);
    };

    fetch(`${COVERS_URL}?t=${Date.now()}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        coverMap = data && data.covers && typeof data.covers === 'object' ? data.covers : {};
        combine();
      })
      .catch(() => {});

    fetch(`${ARTICLES_URL}?t=${Date.now()}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        const list: FeedArticle[] = Array.isArray(data?.articles) ? data.articles : [];
        feedPosts.push(...list.filter((a) => a?.slug && a?.title).map(mapFeedArticle));
        combine();
        setLoadingPosts(false);
      })
      .catch((err) => console.warn('[App] تعذر جلب مقالات الموقع:', err.message));

    const unsubscribe = q.onSnapshot(snapshot => {
      console.log('✅ [App] Firestore onSnapshot: Received update for posts.');
      const dynamicPosts: Post[] = snapshot.docs.map(doc => {
        const data = doc.data();
        const postDate = (data.createdAt && data.createdAt.seconds)
          ? new Date(data.createdAt.seconds * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
          : new Date().toLocaleDateString('en-US', { // Fallback for pending writes
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });

        return {
          id: doc.id,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          imageUrl: data.imageUrl,
          author: analystByline(data.authorName),
          authorTitle: data.authorTitle || ANALYST_TITLE,
          authorId: data.authorId,
          date: postDate,
          tags: data.tags,
          createdAt: data.createdAt,
        };
      });
      console.log('[App] Fetched dynamic posts:', dynamicPosts);
      console.log('[App] Mapped static posts:', mappedStaticPosts);

      firestorePosts = dynamicPosts;
      combine();
      setLoadingPosts(false);
      console.log('[App] Combined and sorted all posts. Loading complete.');
    }, error => {
      console.error("❌ [App] Firestore onSnapshot: Error fetching posts:", error);
      console.log("[App] Firestore onSnapshot: Falling back to static posts due to error.");
      combine(); // fallback to static + feed posts
      setLoadingPosts(false);
    });

    return unsubscribe;

  }, [i18n.language, t]);

  useEffect(() => {
    // --- DEBUG: Log effect for fetching posts ---
    console.log('[App] useEffect (fetchAndCombinePosts) running.');
    const unsubscribe = fetchAndCombinePosts();
    return () => {
      console.log('[App] useEffect (fetchAndCombinePosts) cleanup. Unsubscribing from Firestore.');
      unsubscribe();
    }
  }, [fetchAndCombinePosts]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setTimeout(() => {
      const sections = Array.from(document.querySelectorAll('main section')).filter((el) => el.id !== 'home');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('reveal-in');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
      sections.forEach((el) => {
        el.classList.add('reveal-ready');
        observer.observe(el);
      });
      (window as any).__tadRevealObserver?.disconnect?.();
      (window as any).__tadRevealObserver = observer;
    }, 80);
    return () => {
      window.clearTimeout(timer);
      (window as any).__tadRevealObserver?.disconnect?.();
    };
  }, [route, loadingPosts]);

  useEffect(() => {
    // --- DEBUG: Log effect for global event listeners ---
    console.log('[App] useEffect (event listeners) running to set up mousemove and hashchange.');
    const handleMouseMove = (event: MouseEvent) => {
      const { clientX } = event;
      const { innerWidth } = window;
      const hue = 270 - (clientX / innerWidth) * 60; // Purple to Blue range
      const color = `hsl(${hue}, 90%, 70%)`;
      document.documentElement.style.setProperty('--dynamic-logo-color', color);
    };
    window.addEventListener('mousemove', handleMouseMove);

    const handleHashChange = () => {
      console.log(`[App] Hash changed. New hash: ${window.location.hash}`);
      setRoute(window.location.hash || '#home');
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      console.log('[App] useEffect (event listeners) cleanup.');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    // --- DEBUG: Log effect for auth state listener ---
    console.log('[App] useEffect (onAuthStateChanged) running to set up auth listener.');
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('➡️ [App] onAuthStateChanged listener triggered. User object:', user);
      if (user) {
        console.log('✅ [App] User is signed in. UID:', user.uid);
        const userDocRef = db.collection('users').doc(user.uid);
        try {
          console.log('[App] Fetching user profile from Firestore...');
          const userDoc = await userDocRef.get();

          if (userDoc.exists) {
            console.log('✅ [App] User profile exists in Firestore:', userDoc.data());
            setCurrentUser({ auth: user, profile: userDoc.data() as UserProfileData });
          } else {
            console.log("ℹ️ [App] User profile doesn't exist. Creating a new one (likely for social login)...");

            const newProfile: UserProfileData = {
              displayName: user.displayName,
              email: user.email,
              phoneNumber: user.phoneNumber || null,
              scar: false,
              admin: false,
              createdAt: serverTimestamp(),
            };
            await userDocRef.set(newProfile);
            console.log('✅ [App] New profile created in Firestore.');
            setCurrentUser({ auth: user, profile: { ...newProfile, createdAt: undefined } }); // set client-side user without timestamp
          }
          setAuthModalOpen(false);
        } catch (error) {
          console.error("❌ [App] Error fetching/creating user profile:", error);
        }
      } else {
        console.log('ℹ️ [App] User is signed out.');
        setCurrentUser(null);
      }
    });
    return () => {
      console.log('[App] useEffect (onAuthStateChanged) cleanup. Unsubscribing from auth listener.');
      unsubscribe();
    };
  }, []);

  const handleOpenAuthModal = (view: 'signIn' | 'signUp') => {
    console.log(`[App] Opening Auth Modal with view: ${view}`);
    setAuthModalView(view);
    setAuthModalOpen(true);
  };

  // Sara pages/buttons live deep in the tree and ask for sign-in through this event.
  useEffect(() => {
    const openSignIn = () => handleOpenAuthModal('signIn');
    window.addEventListener('tad:open-auth', openSignIn);
    return () => window.removeEventListener('tad:open-auth', openSignIn);
  }, []);

  const handleCloseAuthModal = () => {
    console.log('[App] Closing Auth Modal.');
    setAuthModalOpen(false);
  };

  const handleSignOut = () => {
    console.log('[App] Signing out user...');
    auth.signOut().catch((error) => console.error('❌ [App] Sign out error', error));
  };

  const handlePostCreated = (postId: string) => {
    console.log(`[App] Post created (ID: ${postId}). Navigating to post page.`);
    window.location.hash = `#/blog/${postId}`;
  }

  const renderContent = () => {
    // --- DEBUG: Log content rendering logic ---
    console.log(`[App] renderContent() called. Page: ${page}, Loading: ${loadingPosts}`);
    if (loadingPosts) {
      return <div className="h-screen flex items-center justify-center text-white">{t('loading')}</div>;
    }

    switch (page) {
      case 'blog':
        return <BlogPage posts={posts} />;
      case 'post':
        return <BlogPostPage postId={params.id!} posts={posts} />;
      case 'create-post':
        if (currentUser && (currentUser.profile.scar || currentUser.profile.admin)) {
          return <CreatePostPage currentUser={currentUser} onPostCreated={handlePostCreated} />;
        }
        // Redirect to home if not logged in or not a scar user
        console.warn(`[App] Unauthorized access to /create-post. Redirecting to #home. User:`, currentUser);
        window.location.hash = '#home';
        return null;
      case 'payment-result':
        return <PaymentResult />;
      case 'chat':
        return <SaraChatPage currentUser={currentUser} />;
      case 'home':
      default:
        return (
          <>
            <Hero />
            <LiveAiPicks />
            <TopTenMarketStats />
            <Features />
            <FeatureList />
            <Pricing />
            <Testimonials />
            <Clients />
            <Support currentUser={currentUser} />
            <Blog posts={posts} />
            <CTA />
          </>
        );
    }
  }

  if (page === 'chat') {
    return (
      <>
        <FraudWarningModal />
        <SaraChatPage currentUser={currentUser} />
      </>
    );
  }

  return (
    <div className="bg-[#0A1021] text-white/80 font-sans flex flex-col min-h-screen">
      <FraudWarningModal />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleCloseAuthModal}
        initialView={authModalView}
      />
      <Header
        currentUser={currentUser}
        onSignInClick={() => handleOpenAuthModal('signIn')}
        onSignUpClick={() => handleOpenAuthModal('signUp')}
        onSignOutClick={handleSignOut}
      />
      <main>
        {renderContent()}
      </main>
      <FraudNoticeBar />
      <Footer />
      <BackToTopButton />
      <AIFloatingWidget />
      <SaraChatWidget />
      <NewsPulse />
    </div>
  );
};

export default App;